import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../lib/api";

type Agent = {
	id: string;
	orgId: string;
	oidcClientId: string;
	displayName: string;
	status: "active" | "paused" | "disabled";
	pausedReason: string | null;
	pausedAt: number | null;
	createdAt: number;
};

type PostbackSecretRow = {
	id: string;
	side: "brand" | "affiliate";
	description: string | null;
	createdAt: number;
	revokedAt: number | null;
};

type PostbackMintResp = { id: string; side: "brand" | "affiliate"; secret: string; note: string };

export function Settings() {
	return (
		<div className="space-y-8 p-6">
			<h1 className="text-xl font-semibold">Settings</h1>
			<AgentsSection />
			<PostbackSecretsSection />
		</div>
	);
}

function AgentsSection() {
	const qc = useQueryClient();
	const { data, isLoading } = useQuery<{ items: Agent[] }>({
		queryKey: ["agents"],
		queryFn: () => api.get("/agents"),
	});

	const pauseMut = useMutation({
		mutationFn: (id: string) => api.post(`/agents/${id}/pause`, {}),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["agents"] }),
	});
	const resumeMut = useMutation({
		mutationFn: (id: string) => api.post(`/agents/${id}/resume`, {}),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["agents"] }),
	});

	if (isLoading) return <section>Loading agents…</section>;
	const items = data?.items ?? [];

	return (
		<section className="space-y-2">
			<h2 className="text-base font-medium">Agents</h2>
			{items.length === 0 && (
				<div className="text-sm text-neutral-500">
					No agents provisioned. They are created via the bootstrap-org tool or admin API.
				</div>
			)}
			<ul className="space-y-2">
				{items.map((a) => (
					<li
						key={a.id}
						className="flex items-center justify-between rounded border border-neutral-200 p-3 text-sm"
					>
						<div>
							<div className="font-medium">{a.displayName}</div>
							<div className="font-mono text-xs text-neutral-500">{a.oidcClientId}</div>
							{a.status === "paused" && a.pausedReason && (
								<div className="mt-1 text-xs text-amber-700">paused: {a.pausedReason}</div>
							)}
						</div>
						<div className="flex items-center gap-2">
							<span className="text-xs">{a.status}</span>
							{a.status === "active" ? (
								<button
									type="button"
									onClick={() => pauseMut.mutate(a.id)}
									className="rounded bg-amber-600 px-3 py-1 text-xs text-white hover:bg-amber-700"
								>
									Pause (kill-switch)
								</button>
							) : (
								<button
									type="button"
									onClick={() => resumeMut.mutate(a.id)}
									className="rounded bg-emerald-600 px-3 py-1 text-xs text-white hover:bg-emerald-700"
								>
									Resume
								</button>
							)}
						</div>
					</li>
				))}
			</ul>
		</section>
	);
}

function PostbackSecretsSection() {
	const qc = useQueryClient();
	const { data, isLoading } = useQuery<{ items: PostbackSecretRow[] }>({
		queryKey: ["postback-secrets"],
		queryFn: () => api.get("/postback-secrets"),
	});

	const [description, setDescription] = useState("");
	const [justMinted, setJustMinted] = useState<PostbackMintResp | null>(null);

	const mintMut = useMutation({
		mutationFn: () =>
			api.post<PostbackMintResp>("/postback-secrets", description ? { description } : {}),
		onSuccess: (resp) => {
			setJustMinted(resp);
			setDescription("");
			qc.invalidateQueries({ queryKey: ["postback-secrets"] });
		},
	});
	const revokeMut = useMutation({
		mutationFn: (id: string) => api.post(`/postback-secrets/${id}/revoke`, {}),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["postback-secrets"] }),
	});

	if (isLoading) return <section>Loading secrets…</section>;
	const items = data?.items ?? [];

	return (
		<section className="space-y-3">
			<h2 className="text-base font-medium">Postback HMAC secrets</h2>
			<p className="text-sm text-neutral-600">
				Generate a shared secret per tracker. Sign each <code>POST /postback</code> body with
				HMAC-SHA256 and pass hex in <code>x-g1-postback-sig</code> header. Source header must match
				your org side (<span className="font-mono">brand</span> or{" "}
				<span className="font-mono">affiliate</span>).
			</p>

			<div className="flex gap-2">
				<input
					placeholder="Description (optional, e.g. Affise integration)"
					value={description}
					onChange={(e) => setDescription(e.target.value)}
					className="flex-1 rounded border px-2 py-1 text-sm"
				/>
				<button
					type="button"
					onClick={() => mintMut.mutate()}
					disabled={mintMut.isPending}
					className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
				>
					Mint new secret
				</button>
			</div>

			{justMinted && (
				<div className="rounded border border-emerald-300 bg-emerald-50 p-3 text-sm">
					<div className="font-medium text-emerald-900">Save now — it will not be shown again:</div>
					<pre className="mt-2 overflow-auto rounded bg-white p-2 font-mono text-xs">
						{justMinted.secret}
					</pre>
					<div className="mt-2 text-xs text-emerald-800">{justMinted.note}</div>
					<button
						type="button"
						onClick={() => setJustMinted(null)}
						className="mt-2 text-xs text-emerald-700 underline"
					>
						I have saved it — dismiss
					</button>
				</div>
			)}

			<ul className="space-y-2">
				{items.map((s) => (
					<li
						key={s.id}
						className="flex items-center justify-between rounded border border-neutral-200 p-3 text-sm"
					>
						<div>
							<div className="font-mono text-xs">{s.id}</div>
							<div className="text-xs text-neutral-500">
								{s.description ?? "(no description)"} ·{" "}
								{new Date(s.createdAt * 1000).toLocaleDateString()}
							</div>
						</div>
						<div className="flex items-center gap-2">
							{s.revokedAt ? (
								<span className="text-xs text-neutral-500">
									revoked {new Date(s.revokedAt * 1000).toLocaleDateString()}
								</span>
							) : (
								<button
									type="button"
									onClick={() => revokeMut.mutate(s.id)}
									className="rounded bg-red-600 px-2 py-1 text-xs text-white hover:bg-red-700"
								>
									Revoke
								</button>
							)}
						</div>
					</li>
				))}
				{items.length === 0 && <li className="text-sm text-neutral-500">No secrets yet.</li>}
			</ul>
		</section>
	);
}
